import { registerAPAndRTAPI } from '@/api/storeManagement/registration';
import { useAppContext } from '@/provider/contexts';
import { useRegistrationState } from '@/provider/registration/registrationContext';
import { useRegistrationLifecycleState } from '@/provider/registration/registrationLifecycleContext';
import {
    APDialogAction,
    APDialogState,
    InfraDevice,
    INPUT_CATEGORY,
    RegisterAPAndRTRequest,
    ValidatedMessage,
} from '@/types/pages/storeManagement/registration/registrationTypes';
import { normalizeEslMac } from '@/utils/inputUtils';
import { ChangeEvent, SyntheticEvent, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const isScannerRawValue = (value: string) => {
    return value.trim().startsWith('+');
};

const isEncodedScannerValue = (value: string) => {
    return value.trim().startsWith('++');
};

const normalizeManualMac = (value: string) => {
    return value.replace(/[^0-9A-Fa-f]/g, '').toUpperCase();
};

const normalizeApMacForSubmit = (value: string) => {
    const trimmed = value.trim();

    if (isEncodedScannerValue(trimmed)) {
        return normalizeEslMac(trimmed);
    }

    if (isScannerRawValue(trimmed)) {
        return '';
    }

    return normalizeManualMac(trimmed);
};

export const AP_INPUT_CONFIG = [
    {
        id: 1,
        category: INPUT_CATEGORY.AP_MAC,
        type: 'text',
        label: 'AP MAC',
        placeholder: '00:00:00:00:00:00',
        key: 'mac',
        slotProps: {
            input: {
                inputProps: {
                    maxLength: 32,
                    pattern: '[+0-9A-Za-z:]*',
                },
            },
        },
        format: (value: unknown) => {
            const rawValue = String(value ?? '');

            if (isScannerRawValue(rawValue)) {
                return rawValue;
            }

            return (
                rawValue
                    .replace(/[^0-9A-Fa-f]/g, '')
                    .toUpperCase()
                    .match(/.{1,2}/g)
                    ?.join(':') ?? ''
            );
        },
    },
    {
        id: 2,
        category: INPUT_CATEGORY.SUB_CHANNEL,
        label: 'Channel',
        placeholder: '1-97',
        type: 'text',
        key: 'channel',
        slotProps: {
            input: {
                inputProps: {
                    maxLength: 2,
                    pattern: '[0-9]*',
                },
            },
        },
        format: (value: unknown) => value ?? '',
    },
    {
        id: 3,
        category: INPUT_CATEGORY.TREE_ID,
        label: 'Tree Id',
        placeholder: '0-254',
        type: 'text',
        key: 'treeId',
        slotProps: {
            input: {
                inputProps: {
                    maxLength: 3,
                    pattern: '[0-9]*',
                },
            },
        },
        format: (value: unknown) => value ?? '',
    },
] as const;

const defaultState: APDialogState = {
    mac: '',
    channel: null,
    treeId: null,
    validatedMessage: {
        mac: '',
        channel: '',
        treeId: '',
    },
};

const reducer: React.Reducer<APDialogState, APDialogAction> = (state, action) => {
    switch (action.type) {
        case 'SET_MAC':
            return { ...state, mac: action.payload };

        case 'SET_CHANNEL':
            return { ...state, channel: action.payload };

        case 'SET_TREE_ID':
            return { ...state, treeId: action.payload };

        case 'SET_VALIDATED_MESSAGE':
            return { ...state, validatedMessage: action.payload };

        case 'SET_EDIT_DATA': {
            const { userset_sub_channel, userset_treeid, mac } = action.payload || {};

            return {
                ...state,
                mac,
                channel: userset_sub_channel,
                treeId: userset_treeid,
            };
        }

        case 'CLEAR_DIALOG_DATA':
            return defaultState;

        default:
            return state;
    }
};

const useAddApHook = () => {
    const { t } = useTranslation('common');
    const { manageStoreId, setNotification } = useAppContext();
    const { state, dispatch } = useRegistrationState();
    const { refreshRegistrationNow } = useRegistrationLifecycleState();

    const [apState, apDispatch] = useReducer(reducer, defaultState);
    const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

    const hasEditData = state.editAPInfo ? Object.keys(state.editAPInfo).length !== 0 : false;

    const normalizedCurrentMac = useMemo(() => normalizeApMacForSubmit(apState.mac), [apState.mac]);

    const isEditDataUnchanged = useMemo(() => {
        if (!hasEditData) return false;

        const editAPInfo = state.editAPInfo as InfraDevice;

        return (
            normalizedCurrentMac === editAPInfo.mac &&
            apState.channel === editAPInfo.userset_sub_channel &&
            apState.treeId === editAPInfo.userset_treeid
        );
    }, [apState.channel, apState.treeId, hasEditData, normalizedCurrentMac, state.editAPInfo]);

    const validateMac = useCallback(
        (mac: string) => {
            const rawMac = normalizeApMacForSubmit(mac);

            if (!rawMac) {
                return t('Required');
            }

            if (rawMac.length !== 12) {
                return t('Must be exactly 12 hexadecimal characters');
            }

            return '';
        },
        [t],
    );

    const validateChannel = useCallback(
        (channel: number | null) => {
            if (channel == null) {
                return t('Required');
            }

            if (Number.isNaN(channel) || channel < 1 || channel > 97) {
                return t('Must be a number between {{min}} and {{max}}', { min: 1, max: 97 });
            }

            return '';
        },
        [t],
    );

    const validateTreeId = useCallback(
        (treeId: number | null) => {
            if (treeId == null) {
                return t('Required');
            }

            if (Number.isNaN(treeId) || treeId < 0 || treeId > 254) {
                return t('Must be a number between {{min}} and {{max}}', { min: 0, max: 254 });
            }

            return '';
        },
        [t],
    );

    const confirmDisabled = useMemo(() => {
        const hasError =
            apState.validatedMessage.mac !== '' ||
            apState.validatedMessage.channel !== '' ||
            apState.validatedMessage.treeId !== '';

        const isEmpty = normalizedCurrentMac === '' || apState.channel === null || apState.treeId === null;

        return isEmpty || hasError || isEditDataUnchanged;
    }, [apState.channel, apState.treeId, apState.validatedMessage, isEditDataUnchanged, normalizedCurrentMac]);

    const closeApDialog = useCallback(() => {
        dispatch({
            type: 'TOGGLE_DIALOG',
            payload: {
                dialog: 'isApOpen',
                isOpen: false,
            },
        });
    }, [dispatch]);

    const resetApDialog = useCallback(() => {
        apDispatch({ type: 'CLEAR_DIALOG_DATA' });
        dispatch({
            type: 'SET_AP_INFO',
            payload: {} as InfraDevice,
        });
    }, [dispatch]);

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;

        if (state.dialogState.isApOpen) {
            timer = setTimeout(() => {
                if (hasEditData) {
                    apDispatch({
                        type: 'SET_EDIT_DATA',
                        payload: state.editAPInfo as InfraDevice,
                    });
                    inputRefs.current.channel?.focus();
                    return;
                }

                inputRefs.current.mac?.focus();
            });
        }

        return () => {
            clearTimeout(timer);
        };
    }, [hasEditData, state.dialogState.isApOpen, state.editAPInfo]);

    const handleValueChange = useCallback((value: string, category: string) => {
        switch (category) {
            case INPUT_CATEGORY.AP_MAC: {
                const nextValue = value.trim();

                if (isScannerRawValue(nextValue)) {
                    apDispatch({
                        type: 'SET_MAC',
                        payload: nextValue,
                    });
                    break;
                }

                apDispatch({
                    type: 'SET_MAC',
                    payload: normalizeManualMac(nextValue),
                });
                break;
            }

            case INPUT_CATEGORY.SUB_CHANNEL: {
                const formatted = value.replace(/[^0-9]/g, '').slice(0, 2);
                apDispatch({ type: 'SET_CHANNEL', payload: formatted ? Number(formatted) : null });
                break;
            }

            case INPUT_CATEGORY.TREE_ID: {
                const formatted = value.replace(/[^0-9]/g, '').slice(0, 3);
                apDispatch({ type: 'SET_TREE_ID', payload: formatted ? Number(formatted) : null });
                break;
            }

            default:
                break;
        }
    }, []);

    const handleChange = useCallback(
        (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, category: string) => {
            handleValueChange(event.target.value, category);
        },
        [handleValueChange],
    );

    const handleApMacScan = useCallback((rawValue: string) => {
        const trimmed = rawValue.trim();

        const normalizedValue = trimmed.startsWith('++')
            ? normalizeEslMac(trimmed)
            : trimmed.replace(/[^0-9A-Fa-f]/g, '').toUpperCase();

        if (!normalizedValue) return;

        apDispatch({
            type: 'SET_MAC',
            payload: normalizedValue,
        });
    }, []);

    const normalizeEncodedMacValue = useCallback((value: string, category: string) => {
        if (category !== INPUT_CATEGORY.AP_MAC) return;

        const normalizedValue = normalizeApMacForSubmit(value);

        if (!normalizedValue) return;

        apDispatch({
            type: 'SET_MAC',
            payload: normalizedValue,
        });
    }, []);

    const handleValidate = useCallback(
        (_: SyntheticEvent, category: string) => {
            const payload: ValidatedMessage = {
                ...apState.validatedMessage,
            };

            switch (category) {
                case INPUT_CATEGORY.AP_MAC:
                    payload.mac = validateMac(apState.mac);
                    break;

                case INPUT_CATEGORY.SUB_CHANNEL:
                    payload.channel = validateChannel(apState.channel);
                    break;

                case INPUT_CATEGORY.TREE_ID:
                    payload.treeId = validateTreeId(apState.treeId);
                    break;

                default:
                    break;
            }

            apDispatch({
                type: 'SET_VALIDATED_MESSAGE',
                payload,
            });
        },
        [apState, validateChannel, validateMac, validateTreeId],
    );

    const isInputDisabled = useCallback(
        (category: string) => {
            return category === INPUT_CATEGORY.AP_MAC && hasEditData;
        },
        [hasEditData],
    );

    const submitApForm = useCallback(async () => {
        const normalizedMac = normalizeApMacForSubmit(apState.mac);

        if (confirmDisabled || !normalizedMac) {
            return;
        }

        closeApDialog();

        dispatch({
            type: 'TOGGLE_LOADING',
            payload: {
                key: 'deviceTableLoading',
                isLoading: true,
            },
        });

        const postData: RegisterAPAndRTRequest = {
            mac: normalizedMac,
            channel: apState.channel,
            treeid: apState.treeId,
            store_id: manageStoreId as number,
            type: 'ap',
        };

        const {
            data: { isSuccess, message },
        } = await registerAPAndRTAPI(postData);

        if (isSuccess) {
            setNotification({ message, severity: 'success' });
            void refreshRegistrationNow({
                clearSelected: true,
                resetTable: true,
            });
        } else {
            setNotification({ message, severity: 'error' });
        }

        dispatch({
            type: 'TOGGLE_LOADING',
            payload: {
                key: 'deviceTableLoading',
                isLoading: false,
            },
        });
    }, [
        apState.channel,
        apState.mac,
        apState.treeId,
        closeApDialog,
        confirmDisabled,
        dispatch,
        refreshRegistrationNow,
        manageStoreId,
        setNotification,
    ]);

    return {
        state,
        apState,
        inputRefs,
        hasEditData,
        confirmDisabled,
        closeApDialog,
        resetApDialog,
        handleChange,
        handleValueChange,
        normalizeEncodedMacValue,
        handleValidate,
        isInputDisabled,
        submitApForm,
        handleApMacScan,
    };
};

export default useAddApHook;
